import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { whatsappLink } from "@/lib/i18n";
import { resolveImageUrl } from "@/lib/media";
import { motion } from "framer-motion";
import { Calendar, Phone, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Doctors = () => {
  const { t, lang } = useLanguage();
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/doctors`)
      .then((res) => setDoctors(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.error("Failed to load doctors:", e);
        setDoctors([]);
      });
  }, []);

  return (
    <section
      id="doctors"
      data-testid="doctors-section"
      className="section-y bg-white"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">{t.doctors.overline}</div>
          <h2
            data-testid="doctors-title"
            className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
          >
            {t.doctors.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">{t.doctors.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {doctors.map((d, i) => (
            <motion.article
              key={d.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`doctor-card-${d.id}`}
              className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-2xl hover:shadow-blue-900/10 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-blue-50">
                <img
                  src={resolveImageUrl(d.photo)}
                  alt={d.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-xs font-semibold text-blue-800">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  {lang === "id" ? "Bersertifikat" : "Certified"}
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-slate-900">
                  {d.name}
                </h3>
                <p className="text-sm text-blue-800 font-medium mt-1">
                  {lang === "id" ? d.specialization_id : d.specialization_en}
                </p>

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2 text-sm text-slate-600">
                  <Calendar className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider">
                      {t.doctors.schedule}
                    </div>
                    <div className="mt-0.5">
                      {lang === "id" ? d.schedule_id : d.schedule_en}
                    </div>
                  </div>
                </div>

                <a
                  href={whatsappLink(
                    lang === "id"
                      ? `Halo, saya ingin membuat janji temu dengan ${d.name}.`
                      : `Hello, I would like to book an appointment with ${d.name}.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`doctor-book-${d.id}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-blue-800 hover:text-blue-900 group/link"
                >
                  <Phone className="h-4 w-4" />
                  {t.doctors.book}
                  <span className="ml-1 transition-transform group-hover/link:translate-x-1">
                    →
                  </span>
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Doctors;
